import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";

export default function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen bg-lm-bg flex flex-col">
      <header className="px-6 md:px-10 h-16 flex items-center">
        <Link to="/" className="inline-flex items-center gap-2 text-lm-text2 hover:text-lm-text text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-8 md:py-12">
        <div className="w-full max-w-md">
          <div className="mb-6 md:mb-8 text-center">
            <Link to="/" className="inline-flex items-center justify-center mb-5" aria-label="Leadmato home">
              <span className="inline-flex items-center justify-center bg-[color:var(--lm-dark)] rounded-lg px-4 py-2.5 shadow-[0_6px_18px_-6px_rgba(31,74,56,0.35)]">
                <img src="/leadmato-logo.png" alt="Leadmato" className="h-7 w-auto select-none" draggable="false" />
              </span>
            </Link>
            <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-lm-text">{title}</h1>
            {subtitle && <p className="mt-2 text-sm md:text-base text-lm-text2">{subtitle}</p>}
          </div>
          <div className="bg-lm-surface border border-lm-border rounded-lg p-6 md:p-8 shadow-[0_8px_24px_-12px_rgba(31,74,56,0.15)]">
            {children}
          </div>
          {footer && <div className="mt-5 text-center text-sm text-lm-text2">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

export function GoogleButton({ label = "Continue with Google" }) {
  const location = useLocation();
  const handleGoogleLogin = () => {
    // Resume wherever ProtectedRoute bounced the user from (e.g. an email
    // "continue" link to /onboarding?job=<id>) instead of always the dashboard.
    const from = location.state?.from;
    const dest = from?.pathname ? from.pathname + (from.search || "") : "/app/find-customers";
    const url = new URL(dest, window.location.origin);
    if (url.hostname.startsWith("www.")) url.hostname = url.hostname.slice(4);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(url.toString())}`;
  };
  return (
    <button
      onClick={handleGoogleLogin}
      data-testid="google-oauth-btn"
      type="button"
      className="w-full inline-flex items-center justify-center gap-3 bg-white border border-lm-border rounded-md px-5 py-3 font-medium text-lm-text hover:border-lm-primary/40 hover:bg-lm-tint/40 transition-all"
    >
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.6-5.2-11.6-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.6-5.6C33.8 6.5 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.3 19 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.6-5.6C33.8 6.5 29.1 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 43.5c5 0 9.6-1.9 13.1-5l-6-5c-2 1.4-4.5 2.3-7.1 2.3-5.3 0-9.8-3.4-11.4-8.1l-6.6 5.1C9.6 39 16.2 43.5 24 43.5z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.3 5.5l6 5C40 34.9 43.5 30 43.5 24c0-1.2-.1-2.4-.4-3.5z" />
      </svg>
      {label}
    </button>
  );
}

export function Divider({ label = "or" }) {
  return (
    <div className="my-4 flex items-center gap-3">
      <div className="flex-1 h-px bg-lm-border" />
      <span className="text-xs font-mono text-lm-text3">{label}</span>
      <div className="flex-1 h-px bg-lm-border" />
    </div>
  );
}

export function Field({ label, id, hint, error, ...props }) {
  const [show, setShow] = useState(false);
  const isPassword = props.type === "password";
  const inputType = isPassword ? (show ? "text" : "password") : props.type;
  return (
    <label className="block mb-3.5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-lm-text tracking-wide">{label}</span>
        {hint && <span className="text-[10px] text-lm-text3">{hint}</span>}
      </div>
      <div className="relative">
        <input
          id={id}
          {...props}
          type={inputType}
          className={
            "w-full px-3.5 py-2.5 rounded-md border border-lm-border bg-white text-sm text-lm-text focus:outline-none focus:border-lm-primary/50 focus:ring-2 focus:ring-lm-primary/10 placeholder:text-lm-text3" +
            (isPassword ? " pr-10" : "")
          }
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            tabIndex={-1}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-lm-text3 hover:text-lm-text"
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
    </label>
  );
}

export function SubmitBtn({ children, loading, ...props }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full inline-flex items-center justify-center gap-2 bg-lm-primary text-white rounded-md px-5 py-3 font-semibold hover:bg-lm-primaryHover transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
      {...props}
    >
      {loading && <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />}
      {children}
    </button>
  );
}
